import { useState, useEffect } from "react";
import { Modal } from "reactstrap";
import { callApi } from "../../../ApiHelper/ApiHelper";
import { apiUrl } from "../../../ApiHelper/ApiUrl";
import { toast } from "react-toastify";

const OPTIONS = [
    {
        value: "Operational",
        label: "Operational",
        badge: "Available",
        badgeColor: "#15803d",
        badgeBg: "#dcfce7",
    },
    {
        value: "Breakdown",
        label: "Breakdown",
        badge: "Unavailable",
        badgeColor: "#b91c1c",
        badgeBg: "#fee2e2",
    },
    {
        value: "Under Service",
        label: "Under Service",
        badge: "Unavailable",
        badgeColor: "#d97706",
        badgeBg: "#fef3c7",
    },
    {
        value: "Idle",
        label: "Idle",
        badge: "Not in use",
        badgeColor: "#475569",
        badgeBg: "#f1f5f9",
    },
];

export default function ChangeStatusModal({ modal, toggle, machineryId, machineryName, currentStatus, onSuccess }) {
    const [isLoading, setIsLoading] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState("Operational");
    const [remarks, setRemarks] = useState("");

    useEffect(() => {
        if (modal && currentStatus) {
            setSelectedStatus(currentStatus);
        }
    }, [modal, currentStatus]);

    const handleClose = () => {
        setRemarks("");
        toggle("status");
    };

    const handleSubmit = () => {
        setIsLoading(true);
        const payload = {
            status: selectedStatus,
            statusRemarks: remarks || null,
        };

        callApi(apiUrl.update_machinery + `?machineryId=${machineryId}`, {
            method: "POST",
            data: payload,
        }).then((res) => {
            setIsLoading(false);
            if (res?.status === true) {
                toast.success(res?.message || "Status updated successfully");
                handleClose();
                onSuccess?.();
            } else {
                toast.error(res?.message || "Failed to update status");
            }
        });
    };

    return (
        <Modal isOpen={modal} toggle={handleClose} centered size="md">
            <div style={{ borderRadius: 12, overflow: "hidden", background: "#fff" }}>
                {/* Header */}
                <div style={{ padding: "24px 28px 16px", borderBottom: "1px solid #f1f5f9" }}>
                    <div className="d-flex align-items-start justify-content-between">
                        <div>
                            <h5 style={{ margin: 0, fontWeight: 800, fontSize: "1.2rem", color: "#0f172a" }}>
                                Change status
                            </h5>
                            <p style={{ margin: "4px 0 0", fontSize: "0.88rem", color: "#64748b" }}>
                                {[machineryName, `currently ${currentStatus || "Operational"}`].filter(Boolean).join(" · ")}
                            </p>
                        </div>
                        <button
                            onClick={handleClose}
                            style={{
                                background: "none",
                                border: "none",
                                fontSize: "1.3rem",
                                color: "#94a3b8",
                                cursor: "pointer",
                                lineHeight: 1,
                                padding: 0,
                            }}
                        >
                            ×
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div style={{ padding: "24px 28px" }}>
                    <div className="d-flex flex-column gap-2 mb-4">
                        {OPTIONS.map((opt) => {
                            const isSelected = selectedStatus === opt.value;
                            return (
                                <label
                                    key={opt.value}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "between",
                                        border: `1.5px solid ${isSelected ? "#1e293b" : "#e2e8f0"}`,
                                        background: isSelected ? "#f8fafc" : "#fff",
                                        borderRadius: 10,
                                        padding: "14px 18px",
                                        cursor: "pointer",
                                        transition: "all 0.15s",
                                        margin: 0,
                                    }}
                                >
                                    <div className="d-flex align-items-center gap-3 flex-grow-1">
                                        <input
                                            type="radio"
                                            name="machineryStatus"
                                            value={opt.value}
                                            checked={isSelected}
                                            onChange={() => setSelectedStatus(opt.value)}
                                            style={{
                                                width: 18,
                                                height: 18,
                                                accentColor: "#1e293b",
                                                cursor: "pointer",
                                            }}
                                        />
                                        <span style={{ fontSize: "1rem", fontWeight: 700, color: "#1e293b" }}>
                                            {opt.label}
                                        </span>
                                    </div>
                                    <span
                                        style={{
                                            background: opt.badgeBg,
                                            color: opt.badgeColor,
                                            fontSize: "0.82rem",
                                            fontWeight: 700,
                                            padding: "4px 12px",
                                            borderRadius: 20,
                                            border: `1px solid ${opt.badgeColor}20`,
                                        }}
                                    >
                                        {opt.badge}
                                    </span>
                                </label>
                            );
                        })}
                    </div>

                    {/* Remarks Input */}
                    <div>
                        <label
                            style={{
                                display: "block",
                                fontSize: "0.82rem",
                                fontWeight: 800,
                                letterSpacing: "0.08em",
                                textTransform: "uppercase",
                                color: "#64748b",
                                marginBottom: 8,
                            }}
                        >
                            Remarks
                        </label>
                        <input
                            type="text"
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            placeholder="Reason for the change"
                            style={{
                                width: "100%",
                                border: "1px solid #cbd5e1",
                                borderRadius: 8,
                                padding: "10px 14px",
                                fontSize: "0.95rem",
                                color: "#1e293b",
                                outline: "none",
                                background: "#fff",
                            }}
                        />
                    </div>
                </div>

                {/* Footer */}
                <div
                    style={{
                        padding: "16px 28px",
                        borderTop: "1px solid #f1f5f9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 10,
                    }}
                >
                    <button
                        onClick={handleClose}
                        style={{
                            background: "#fff",
                            border: "1px solid #cbd5e1",
                            borderRadius: 8,
                            padding: "9px 22px",
                            fontSize: "0.95rem",
                            fontWeight: 600,
                            color: "#334155",
                            cursor: "pointer",
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isLoading}
                        style={{
                            background: isLoading ? "#94a3b8" : "#0f5132",
                            border: "none",
                            borderRadius: 8,
                            padding: "9px 22px",
                            fontSize: "0.95rem",
                            fontWeight: 600,
                            color: "#fff",
                            cursor: isLoading ? "not-allowed" : "pointer",
                        }}
                    >
                        {isLoading ? "Saving…" : "Update status"}
                    </button>
                </div>
            </div>
        </Modal>
    );
}
